import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import React, { useContext, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useBoolean, useEffectOnce } from 'react-use'

import Icon from 'components/Icon'
import {
  AuthenticationFormValues,
  AWSAuthenticationType,
  getAuthenticationFormValidationSchema,
  getAWSOnboardingAuthenticationDefaultValues,
  isSelectedObservabilityToolAuthenticated,
  getAWSDefaultValuesForReset
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolAuthenticateAndSaveButton } from 'components/status-checks/common/ObservabilityToolAuthenticateAndSaveButton'
import { ObservabilityToolAuthenticationStatus } from 'components/status-checks/common/ObservabilityToolAuthenticationStatus'
import { ObservabilityToolAWSAuthenticationDetails } from 'components/status-checks/form/authentication/aws/ObservabilityToolAWSAuthenticationDetails'
import { buildTeamExternalIntegration } from 'components/status-checks/status-checks-form-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useTranslation } from 'hooks/use-translation'
import {
  TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus
} from 'types/api/status-checks/status-check'

export const AWSOnboardingForm: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  selectedAwsAccountId?: string
  handleNext?: (accountId: string) => void
  hasIntroText?: boolean
  onSuccess?: () => void
}> = ({
  externalIntegrations,
  selectedAwsAccountId,
  handleNext = () => {},
  hasIntroText = true,
  onSuccess = () => {}
}) => {
  const { trans } = useTranslation()
  const [isAuthenticationValid, setIsAuthenticationValid] = useBoolean(false)
  const [isAuthenticationSaving, setIsAuthenticationSaving] = useBoolean(false)

  const authenticationFormValidationSchema =
    getAuthenticationFormValidationSchema(externalIntegrations)

  const defaultValues = getAWSOnboardingAuthenticationDefaultValues(
    externalIntegrations,
    selectedAwsAccountId
  )

  const form = useForm<AuthenticationFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(authenticationFormValidationSchema)
  })

  const { formState, getValues, setValue, watch, reset } = form

  // Watches selected form values and re-renders when changed
  const [
    observabilityTool,
    awsAuthenticationType,
    awsIamRoleArn,
    awsAccessKeyID,
    awsSecretAccessKey
  ] = watch([
    'observabilityTool',
    'awsAuthenticationType',
    'awsIamRoleArn',
    'awsAccessKeyID',
    'awsSecretAccessKey'
  ])

  const { isCreatingAuthenticationOnly, isEdit, isDrawerFlow } = useContext(
    ObservabilityToolAuthenticationContext
  )

  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  useEffectOnce(() => {
    if (isCreatingAuthenticationOnly && !isEdit) {
      const externalIntegrations = getAvailableExternalIntegrations(false)
      reset(getAWSDefaultValuesForReset(externalIntegrations))
    }
  })

  const hasAWSErrors = useMemo(() => {
    // We build the integration here to ensure we have the latest form values
    const integration = buildTeamExternalIntegration(getValues())

    if (awsAuthenticationType === AWSAuthenticationType.ServiceAccount) {
      return (
        Boolean(formState.errors.authenticationUrl) ||
        Boolean(formState.errors.awsAccessKeyID) ||
        Boolean(formState.errors.awsSecretAccessKey) ||
        Boolean(formState.errors.domain) ||
        !awsAccessKeyID ||
        !awsSecretAccessKey ||
        !integration.integrationSpecificValues?.AWS_ACCESS_KEY_ID ||
        !integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
      )
    }

    return (
      !awsIamRoleArn ||
      Boolean(formState.errors.awsIamRoleArn) ||
      !integration.integrationSpecificValues?.AWS_IAM_ROLE_ARN
    )
  }, [
    getValues,
    awsAuthenticationType,
    awsIamRoleArn,
    formState.errors.awsIamRoleArn,
    formState.errors.authenticationUrl,
    formState.errors.awsAccessKeyID,
    formState.errors.awsSecretAccessKey,
    formState.errors.domain,
    awsAccessKeyID,
    awsSecretAccessKey
  ])

  const handleTestTeamExternalIntegrationError = () => {
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.UnAuthenticated
    )
  }

  const handleTestTeamExternalIntegrationSuccess = async () => {
    setIsAuthenticationValid(true)
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.Authenticated
    )

    onSuccess()
  }

  const isAuthenticated = useMemo(() => {
    return isSelectedObservabilityToolAuthenticated(
      observabilityTool,
      externalIntegrations
    )
  }, [externalIntegrations, observabilityTool])

  const hasAWSAuthentications = Boolean(observabilityTool?.domains?.length)

  return (
    <FormProvider {...form}>
      <Grid
        container
        {...(isDrawerFlow && {
          sx: {
            mr: 6
          }
        })}
      >
        <Grid item lg={9} xs={12}>
          <Stack spacing={4}>
            <Stack spacing={2}>
              {hasIntroText && hasAWSAuthentications && (
                <Typography mb={2}>
                  {trans(
                    'forms.save_status_check.select_observability_tool_and_domain.observability_tool_aws_account_id.prompt'
                  )}
                </Typography>
              )}
              {hasIntroText && !hasAWSAuthentications && (
                <Typography>
                  {trans(
                    'getting_started.aws_onboarding_form.no_existing_aws_integrations.title'
                  )}
                </Typography>
              )}
              <ObservabilityToolAWSAuthenticationDetails
                isAuthenticationValid={isAuthenticationValid}
                setIsAuthenticationValid={setIsAuthenticationValid}
              />
            </Stack>
            <Box>
              {!isAuthenticated && (
                <ObservabilityToolAuthenticateAndSaveButton
                  disabled={hasAWSErrors}
                  externalIntegrations={externalIntegrations}
                  isAuthenticationSaving={isAuthenticationSaving}
                  setIsAuthenticationSaving={setIsAuthenticationSaving}
                  onTestError={handleTestTeamExternalIntegrationError}
                  onTestSuccess={handleTestTeamExternalIntegrationSuccess}
                />
              )}
              {isAuthenticated && (
                <Button
                  data-testid="observability-tool-authentication-next-button"
                  endIcon={<Icon icon="arrowRight" />}
                  variant="contained"
                  onClick={() => handleNext(observabilityTool.selectedDomain)}
                >
                  {trans('next')}
                </Button>
              )}
            </Box>
            <ObservabilityToolAuthenticationStatus
              isLoading={isAuthenticationSaving}
            />
          </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
